import React, { useState } from 'react';

function isControlKey(e: React.KeyboardEvent<HTMLInputElement>): boolean {
  return e.ctrlKey || e.metaKey || e.altKey || e.key.length > 1;
}

/**
 * Validates if a string represents a valid numeric format for float input.
 * Matches: optional minus, digits, optional decimal point with digits.
 */
export function isValidFloatNumericString(value: string): boolean {
  const numericPattern = /^-?\d*\.?\d*$/;
  return numericPattern.test(value);
}

/**
 * Creates a change handler for float input with range validation.
 * Returns the validated value or null if the input should be rejected.
 */
export function validateFloatInput(
  inputValue: string,
  min: number,
  max: number,
): { valid: true; value: string | number } | { valid: false } {
  // Allow empty or minus sign for typing
  if (inputValue === '' || inputValue === '-') {
    return { valid: true, value: inputValue };
  }

  // Validate that the entire string is a valid number format
  if (!isValidFloatNumericString(inputValue)) {
    return { valid: false };
  }

  // Allow partial input like "36." for typing decimals
  if (inputValue.endsWith('.')) {
    return { valid: true, value: inputValue };
  }

  const numericValue = parseFloat(inputValue);
  if (!Number.isNaN(numericValue) && numericValue >= min && numericValue <= max) {
    return { valid: true, value: inputValue };
  }

  return { valid: false };
}

/**
 * Validates if a string represents a valid integer format for input.
 * Only validates format and max during typing - min should be validated on blur/submit.
 * @param min - Used to determine if negative sign is allowed (defaults to 0, meaning no negatives)
 * @param max - Maximum value (validated during typing since exceeding won't fix by typing more)
 */
export function validateIntegerInput(inputValue: string, min?: number, max?: number): { valid: true; value: string } | { valid: false } {
  const minValue = min ?? 0;

  // Allow empty for easy typing/clearing
  if (inputValue === '') {
    return { valid: true, value: '' };
  }

  // Allow minus sign alone for typing negative numbers (only if min < 0)
  if (inputValue === '-' && minValue < 0) {
    return { valid: true, value: '-' };
  }

  // Only allow integer format (optional minus followed by digits, minus only if min < 0)
  const integerPattern = minValue < 0 ? /^-?\d+$/ : /^\d+$/;
  if (!integerPattern.test(inputValue)) {
    return { valid: false };
  }

  // Only validate max during typing (exceeding max won't fix itself by typing more digits)
  // Min validation should happen on blur/submit since typing more digits may satisfy min
  // e.g., min=100: typing "1" should be allowed so user can continue to "150"
  if (max !== undefined) {
    const numericValue = parseInt(inputValue, 10);
    if (numericValue > max) {
      return { valid: false };
    }
  }

  return { valid: true, value: inputValue };
}

export function useNumberInput(initialValue: number, name?: string, float = false) {
  const [value, setValue] = useState(initialValue);

  return {
    value,
    setValue,
    reset: () => setValue(initialValue),
    bind: {
      value,
      name,
      onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (isControlKey(e)) {
          return;
        }
        const allowed = float ? /^[-.\d]$/ : /^\d$/;
        if (!allowed.test(e.key)) {
          e.preventDefault();
        }
      },
      onChange: (event: any) => {
        setValue(float ? parseFloat(event.target.value) : parseInt(event.target.value, 10));
      },
      setValue: (val: number) => setValue(val),
    },
  };
}

export function useFloatInput(initialValue: number | string, min: number, max: number, name?: string) {
  const [value, setValue] = useState<string | number>(initialValue);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const result = validateFloatInput(e.target.value, min, max);
    if (result.valid) {
      setValue(result.value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (isControlKey(e)) {
      return;
    }
    if (!/^[-.\d]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return {
    value,
    setValue,
    reset: () => setValue(initialValue),
    bind: {
      name,
      value,
      onKeyDown: handleKeyDown,
      onChange: handleChange,
      setValue: (val: number) => setValue(val),
    },
  };
}
